/**
 * Feature E05-F04 — "Admin: вопросник и position types" request/response contracts.
 *
 * 1:1 port of two legacy n8n CRUD groups behind the single admin Settings screen:
 *   - KQ  (docs/source-n8n/reference/KQ.md)  — `manage_kickoff_questions`
 *         get_questions / create_question / update_question / delete_question
 *         over `kickoff_questions` (migration 019).
 *   - PT  (docs/source-n8n/reference/PT.md)  — `manage_position_types`
 *         get_types / create_type / update_type / delete_type
 *         over `position_types` (migration 013).
 *
 * Both are GLOBAL reference tables (no project/owner) — admin-only writes, soft
 * delete (`is_active=false`), and the v2 REST replacement of the legacy
 * string-concatenated SQL with Prisma's parametrized queries.
 *
 * Invariants carried over unchanged (see KQ.md / PT.md gotchas):
 *   - GET returns ONLY `is_active=true`:
 *       kickoff_questions ORDER BY sort_order, id;
 *       position_types    ORDER BY sort_order, name.
 *   - kickoff question: `category` + `text` REQUIRED, `ai_instruction` optional
 *     (default ''), `sort_order` int (default 0).
 *   - position type: `name` REQUIRED, `questions` is a JSONB array of
 *     `{ text, category:'Position-Specific' }`; empty rows are dropped.
 *   - delete is SOFT (UPDATE is_active=false), never a physical DELETE.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ===========================================================================
// Position-type extra questions — the JSONB array element
// ===========================================================================
/** The fixed category every position-type extra question carries (savePosType). */
export const POSITION_SPECIFIC_CATEGORY = 'Position-Specific';
/**
 * One extra question stored inside `position_types.questions` (jsonb array).
 * Legacy `savePosType`: `{ text, category: 'Position-Specific' }`. `category`
 * defaults to the fixed constant and is the only category these ever use.
 */
export const positionTypeQuestionSchema = z.object({
    text: z.string(),
    category: z.string().default(POSITION_SPECIFIC_CATEGORY),
});
// ===========================================================================
// T01 — kickoff_questions DTO + CRUD bodies (KQ group)
// ===========================================================================
/**
 * One kickoff question row (legacy `KQ: Get All` SELECT):
 *   SELECT id, category, text, ai_instruction, sort_order, is_active
 * `ai_instruction` is a content field (instruction for the AI on how to use the
 * answer), NOT an LLM call here; nullable in the DB, '' default.
 */
export const kickoffQuestionSchema = z.object({
    id: z.number().int(),
    category: z.string(),
    text: z.string(),
    /** Optional AI usage hint; null/'' when not set. */
    ai_instruction: z.string().nullable(),
    /** Sort key; nullable in the schema (treated as 0). */
    sort_order: z.number().int().nullable(),
    /** Nullable in the schema; the list only ever returns true. */
    is_active: z.boolean().nullable(),
});
/** GET /admin/kickoff-questions — only active, ORDER BY sort_order, id. */
export const kickoffQuestionsListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    questions: z.array(kickoffQuestionSchema),
});
/**
 * POST body for create_question. `category` + `text` are REQUIRED (legacy SPA:
 * "Category and text are required"); `ai_instruction` optional (default ''),
 * `sort_order` an int default 0. `.strict()` rejects forged columns.
 */
export const kickoffQuestionCreateBodySchema = z
    .object({
    category: z.string().min(1, 'Category and text are required'),
    text: z.string().min(1, 'Category and text are required'),
    ai_instruction: z.string().optional().default(''),
    sort_order: z.number().int().optional().default(0),
})
    .strict();
/**
 * PATCH body for update_question. All four content fields REQUIRED in the legacy
 * Save handler (the dialog always sends every field), but `ai_instruction` may be
 * empty. We keep `category`/`text` non-empty (same validation as create) and
 * allow empty `ai_instruction`. The id is the `:id` path param, not the body.
 */
export const kickoffQuestionUpdateBodySchema = z
    .object({
    category: z.string().min(1, 'Category and text are required'),
    text: z.string().min(1, 'Category and text are required'),
    ai_instruction: z.string().optional().default(''),
    sort_order: z.number().int().optional().default(0),
})
    .strict();
/** POST create response — echoes the created row (legacy RETURNING). */
export const kickoffQuestionCreateResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    question: kickoffQuestionSchema.pick({
        id: true,
        category: true,
        text: true,
        ai_instruction: true,
        sort_order: true,
    }),
});
/** PATCH update / DELETE response — fixed `{ success:true }` (legacy parity). */
export const adminMutationOkResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
});
// ===========================================================================
// T02 — position_types DTO + CRUD bodies (PT group)
// ===========================================================================
/**
 * One position type row (legacy `PT: Get Types` SELECT):
 *   SELECT id, name, questions, is_active, sort_order
 * `questions` is jsonb (array of `{ text, category }`), returned already parsed.
 */
export const positionTypeSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    /** jsonb array; coerced to the typed question list (non-array → []). */
    questions: z.array(positionTypeQuestionSchema),
    is_active: z.boolean().nullable(),
    sort_order: z.number().int().nullable(),
});
/** GET /admin/position-types — only active, ORDER BY sort_order, name. */
export const positionTypesListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    types: z.array(positionTypeSchema),
});
/**
 * Body for create_type / update_type (`PT: Save Type`). `name` REQUIRED
 * (legacy SPA: "Name is required"); `questions` an array of
 * `{ text, category:'Position-Specific' }`. Empty-text rows are dropped before
 * validation by `sanitizePositionTypeQuestions` (the legacy SPA already filters
 * empty inputs); we still allow an empty array. `.strict()` rejects forged keys.
 */
export const positionTypeSaveBodySchema = z
    .object({
    name: z.string().min(1, 'Name is required'),
    questions: z.array(positionTypeQuestionSchema).optional().default([]),
})
    .strict();
/** POST create_type response — `{ success:true, id, name }` (legacy RETURNING). */
export const positionTypeCreateResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    id: z.number().int(),
    name: z.string(),
});
// ===========================================================================
// Shared path param + pure helpers (testable without a DB)
// ===========================================================================
/** Numeric `:id` path param (questionId / typeId). Coerced from the URL string. */
export const adminIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
});
/**
 * Drop empty-text rows and force `category:'Position-Specific'` on every kept
 * question (legacy `savePosType`: `if (text) questions.push({ text, category })`).
 * Pure — used by the route before validation AND by the UI dialog.
 */
export function sanitizePositionTypeQuestions(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const q of raw) {
        const text = typeof q?.text === 'string' ? q.text.trim() : '';
        if (text.length === 0)
            continue; // empty rows ignored (acceptance T04)
        out.push({ text, category: POSITION_SPECIFIC_CATEGORY });
    }
    return out;
}
/**
 * Coerce a raw jsonb `questions` value (array, JSON string, or junk) into the
 * typed question list (legacy `renderPositionTypes`: array | JSON.parse | []).
 * `{ text }` strings without a category default to 'Position-Specific'; a bare
 * string element (legacy `q.text || q`) becomes `{ text: <string> }`.
 */
export function normalizePositionTypeQuestions(raw) {
    let arr = raw;
    if (typeof raw === 'string') {
        try {
            arr = JSON.parse(raw);
        }
        catch {
            return [];
        }
    }
    if (!Array.isArray(arr))
        return [];
    const out = [];
    for (const q of arr) {
        if (typeof q === 'string') {
            out.push({ text: q, category: POSITION_SPECIFIC_CATEGORY });
        }
        else if (q && typeof q === 'object' && typeof q.text === 'string') {
            const cat = q.category;
            out.push({
                text: q.text,
                category: typeof cat === 'string' && cat.length > 0 ? cat : POSITION_SPECIFIC_CATEGORY,
            });
        }
    }
    return out;
}
/** Toast shown by the kickoff-question dialog when category/text are blank. */
export const KQ_REQUIRED_MESSAGE = 'Category and text are required';
/** Toast shown by the position-type dialog when name is blank. */
export const PT_NAME_REQUIRED_MESSAGE = 'Name is required';
/**
 * Group questions by `category` in FIRST-APPEARANCE order (legacy
 * `renderKickoffQuestions`: a category enters `categoryOrder` the first time a
 * question references it). Pure — the Questionnaire tab renders groups in this
 * exact order. Returns an array of `{ category, questions }` preserving order.
 */
export function groupKickoffQuestionsByCategory(questions) {
    const order = [];
    const byCategory = new Map();
    for (const q of questions) {
        if (!byCategory.has(q.category)) {
            byCategory.set(q.category, []);
            order.push(q.category);
        }
        byCategory.get(q.category).push(q);
    }
    return order.map((category) => ({ category, questions: byCategory.get(category) }));
}
//# sourceMappingURL=admin-questionnaire.js.map